// 创建预订
import { MemberLevel, OccType } from '@/config';
import { ReserveCreateAction } from '@/redux/actions/reservation/constant';
import { DateType } from '@/utils/date';
import {
  CommonBase,
  RentTimeInfo,
  SourceChannelInfo,
  ChannelItem,
  StrategyItem,
  ConfigRoomPrice,
  RoomCategory,
  SelectRoomItem,
  RentRoomInfo,
  RemarkInfo,
  NoticeInfo,
} from './base.d';

export enum DetectMethod {
  Auto = 'auto', // 会员自动判断
  Manual = 'manual', // 会员手动
}

export interface CustomerInfo {
  name: string;
  mobile: string;
  email: string;
  vipDetectMethod: DetectMethod;
  otaOrderNo?: string;
  wyuId?: string;
}

export interface CommonInfo extends CommonBase {
  lastAuditDate?: DateType; // 上次夜审结束时间
  appendOrderId?: string; // 追加预订 订单id
  tempOrderId?: string; // 暂存编辑 订单id
}

export interface MainState {
  commonInfo: CommonInfo; // 基础信息
  customerInfo: CustomerInfo; // 预订人信息
  dayTypeTimeInfo: RentTimeInfo; // 日租
  hourTypeTimeInfo: RentTimeInfo; // 钟点
  channelInfo: SourceChannelInfo; // 客源渠道
  remarkInfo: RemarkInfo; // 备注
  defaultSelectRoom: SelectRoomItem[];
}

export interface ReducerState extends MainState {
  isInit: boolean; // 初始化
  isLoading: boolean; // 加载中
  isModify: boolean; // 是否有修改
  channelList: ChannelItem[]; // 渠道列表
  strategyList: StrategyItem[]; // 策略列表
  allRoomList: RoomCategory[]; // 可选房间
  roomNoticeData: NoticeInfo;
  roomPriceList: ConfigRoomPrice[]; // 房价信息
  selectRoomList: SelectRoomItem[]; // 已选房间
  rentRoomInfos: RentRoomInfo[]; // 入住房信息
}

const initialState: Partial<ReducerState> = {
  isInit: false,
  isLoading: true,
  isModify: false,
  commonInfo: {
    occType: OccType.DayUse,
    memberLevel: MemberLevel.Common,
  },
};

type ActionHandler<T> = (state: ReducerState, action: { payload: T }) => ReducerState;

const reducerMap = {
  [ReserveCreateAction.initDefaultState]: <ActionHandler<Partial<ReducerState>>>((
    state,
    { payload },
  ) => {
    return { ...state, ...payload };
  }),
  [ReserveCreateAction.setLoadingStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, isLoading: payload };
  }),
  [ReserveCreateAction.setInitStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, isInit: payload };
  }),
  [ReserveCreateAction.setModifyStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, isModify: payload };
  }),
  [ReserveCreateAction.setCommonData]: <ActionHandler<Partial<CommonInfo>>>((
    state,
    { payload },
  ) => {
    const { commonInfo } = state;
    return { ...state, commonInfo: { ...commonInfo, ...payload } };
  }),
  [ReserveCreateAction.setCustomerData]: <ActionHandler<Partial<CustomerInfo>>>((
    state,
    { payload },
  ) => {
    const { customerInfo } = state;
    return { ...state, customerInfo: { ...customerInfo, ...payload } };
  }),
  [ReserveCreateAction.setDayTimeData]: <ActionHandler<Partial<RentTimeInfo>>>((
    state,
    { payload },
  ) => {
    const { dayTypeTimeInfo } = state;
    return { ...state, dayTypeTimeInfo: { ...dayTypeTimeInfo, ...payload } };
  }),
  [ReserveCreateAction.setHourTimeData]: <ActionHandler<Partial<RentTimeInfo>>>((
    state,
    { payload },
  ) => {
    const { hourTypeTimeInfo } = state;
    return { ...state, hourTypeTimeInfo: { ...hourTypeTimeInfo, ...payload } };
  }),
  [ReserveCreateAction.setChannelData]: <ActionHandler<Partial<SourceChannelInfo>>>((
    state,
    { payload },
  ) => {
    const { channelInfo } = state;
    return { ...state, channelInfo: { ...channelInfo, ...payload } };
  }),
  [ReserveCreateAction.setChannelList]: <ActionHandler<ChannelItem[]>>((state, { payload }) => {
    return { ...state, channelList: payload };
  }),
  [ReserveCreateAction.setStrategyList]: <ActionHandler<StrategyItem[]>>((state, { payload }) => {
    return { ...state, strategyList: payload };
  }),
  [ReserveCreateAction.setAllRoomList]: <ActionHandler<RoomCategory[]>>((state, { payload }) => {
    return { ...state, allRoomList: payload };
  }),
  [ReserveCreateAction.setRoomNotice]: <ActionHandler<NoticeInfo>>((state, { payload }) => {
    return { ...state, roomNoticeData: payload };
  }),
  [ReserveCreateAction.setCheckedList]: <ActionHandler<SelectRoomItem[]>>((state, { payload }) => {
    return { ...state, selectRoomList: payload };
  }),
  [ReserveCreateAction.setRentRoomList]: <ActionHandler<RentRoomInfo[]>>((state, { payload }) => {
    return { ...state, rentRoomInfos: payload };
  }),
  [ReserveCreateAction.setRoomPriceList]: <ActionHandler<ConfigRoomPrice[]>>((
    state,
    { payload },
  ) => {
    return { ...state, roomPriceList: payload };
  }),
  [ReserveCreateAction.setRemarkInfo]: <ActionHandler<Partial<RemarkInfo>>>((
    state,
    { payload },
  ) => {
    const { remarkInfo } = state;
    return { ...state, remarkInfo: { ...remarkInfo, ...payload } };
  }),
};

export const reducerMapper = (state = initialState, action) => {
  const actionType = action.type;
  const reducer = reducerMap[actionType];
  if (typeof reducer === 'function') {
    return reducer(<ReducerState>state, action);
  }
  return state;
};

export const storeName = 'createReserve';
